import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Search, Edit2, Trash2, UserCheck, Briefcase, Clock, ArrowUpDown, CalendarDays, Save, Loader2 } from 'lucide-react';
const DAY_KEYS = [['sun','Sun'],['mon','Mon'],['tue','Tue'],['wed','Wed'],['thu','Thu'],['fri','Fri'],['sat','Sat']];
import { toast } from 'sonner';
import logger from '../utils/logger';

const SERVICE_LABELS = { day_program: 'Explore Program', in_home: 'IHCS', employment: 'Employment', private_pay: 'Private Pay' };
const SERVICE_BADGE = {
  day_program: 'bg-[#EDE7F6] text-[#6B3FA0] border border-[#9B7ED8]',
  in_home: 'bg-[#E8F5E9] text-[#1B5E20] border border-[#81C784]',
  employment: 'bg-[#FFF3E0] text-[#E65100] border border-[#FFB74D]',
};
const EMPLOYMENT_TYPES = { development_services: 'Development Services (The Fields Method)', job_support: 'Job Support Services' };

const emptyForm = { name: '', notes: '', service_lines: [], authorized_hours_ihcs: '', authorized_hours_employment: '', authorized_hours_day_program: '', job_site: '', employment_type: '' };

// Tiny utilization bar — green/amber/red based on % used
function UtilBar({ pct, over }) {
  const safePct = Math.min(100, Math.max(0, pct || 0));
  const color = over ? '#D9534F' : pct >= 85 ? '#E65100' : pct >= 60 ? '#FF9800' : '#43A047';
  return (
    <div className="h-1.5 rounded-full bg-[#EDE7F6] overflow-hidden w-full" title={`${pct}% used`}>
      <div className="h-full rounded-full transition-all" style={{ width: `${safePct}%`, background: color }} />
    </div>
  );
}

function UtilRow({ icon, label, util, color }) {
  if (!util || !util.tracked) return null;
  return (
    <div className="space-y-0.5" data-testid={`util-row-${util.service_line}`}>
      {/* Weekly */}
      {util.auth_hours > 0 && (
        <div className="flex items-center gap-2 text-[11px] text-[#5A4D6E]">
          <Clock className="h-3 w-3 shrink-0" style={{ color }} />
          <span className="font-medium shrink-0" style={{ minWidth: 36 }}>{label}:</span>
          <span className="font-mono tabular-nums shrink-0">
            {util.used_hours}h / {util.auth_hours}h
          </span>
          <span className="opacity-60 shrink-0">·</span>
          <span className="font-mono tabular-nums opacity-70 shrink-0">
            {util.used_units}/{util.auth_units}u
          </span>
          <span className="flex-1 min-w-[40px]"><UtilBar pct={util.pct} over={util.over} /></span>
          <span className={`font-bold tabular-nums shrink-0 ${util.over ? 'text-[#D9534F]' : util.pct >= 85 ? 'text-[#E65100]' : ''}`} style={{ minWidth: 36, textAlign: 'right' }}>
            {util.pct}%
          </span>
        </div>
      )}
      {/* Period (if configured) */}
      {util.period_active && (
        <div className="flex items-center gap-2 text-[10px] text-[#5A4D6E] pl-5" data-testid={`util-period-${util.service_line}`}>
          <span className="opacity-70 italic shrink-0">Period:</span>
          <span className="font-mono tabular-nums shrink-0">
            {util.period_used_hours}h / {util.period_auth_hours}h
          </span>
          <span className="opacity-60 shrink-0">(used {util.already_utilized_hours}h ext)</span>
          <span className="flex-1 min-w-[40px]"><UtilBar pct={util.period_pct} over={util.period_over} /></span>
          <span className={`font-bold tabular-nums shrink-0 ${util.period_over ? 'text-[#D9534F]' : util.period_pct >= 85 ? 'text-[#E65100]' : ''}`} style={{ minWidth: 36, textAlign: 'right' }}>
            {util.period_pct}%
          </span>
        </div>
      )}
    </div>
  );
}

function AuthPeriodEditor({ serviceLine, label, accentColor, value, onChange, onSave, onClear, busy, disabled }) {
  const v = value || {};
  const projUsedH = (parseFloat(v.already_utilized_hours) || 0);
  const totalH = (parseFloat(v.auth_period_hours) || 0);
  const projPct = totalH > 0 ? Math.min(100, (projUsedH / totalH) * 100) : 0;
  const totalUnits = totalH > 0 ? (totalH * 4).toFixed(0) : '';
  const usedUnits = projUsedH > 0 ? (projUsedH * 4).toFixed(0) : '';
  return (
    <div className="rounded-md border border-dashed p-2.5 mt-2 space-y-2 bg-white/60" style={{ borderColor: accentColor + '80' }} data-testid={`auth-period-${serviceLine}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: accentColor }}>
          {label} · Authorization Period
        </p>
        <span className="text-[10px] text-[#8E85A0]">YTD cap (annual / quarterly waiver)</span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-[#5A4D6E]">Period Start</Label>
          <Input type="date" value={v.period_start || ''} onChange={e => onChange('period_start', e.target.value)}
            className="border-[#DED8EB] h-8 text-xs" disabled={disabled} data-testid={`period-start-${serviceLine}`} />
        </div>
        <div>
          <Label className="text-[10px] text-[#5A4D6E]">Period End</Label>
          <Input type="date" value={v.period_end || ''} onChange={e => onChange('period_end', e.target.value)}
            className="border-[#DED8EB] h-8 text-xs" disabled={disabled} data-testid={`period-end-${serviceLine}`} />
        </div>
        <div>
          <Label className="text-[10px] text-[#5A4D6E]">Total Auth Hours <span className="opacity-60 font-mono">{totalUnits ? `(${totalUnits}u)` : ''}</span></Label>
          <Input type="number" step="0.25" value={v.auth_period_hours ?? ''} onChange={e => onChange('auth_period_hours', e.target.value)}
            placeholder="e.g. 2080" className="border-[#DED8EB] h-8 text-xs font-mono" disabled={disabled} data-testid={`period-total-${serviceLine}`} />
        </div>
        <div>
          <Label className="text-[10px] text-[#5A4D6E]">
            Already Utilized
            <span className="opacity-60 font-mono">{usedUnits ? ` (${usedUnits}u)` : ''}</span>
          </Label>
          <Input type="number" step="0.25" value={v.already_utilized_hours ?? ''} onChange={e => onChange('already_utilized_hours', e.target.value)}
            placeholder="0" className="border-[#DED8EB] h-8 text-xs font-mono" disabled={disabled} data-testid={`period-used-${serviceLine}`} />
        </div>
      </div>

      {totalH > 0 && (
        <div className="text-[10px] space-y-1">
          <div className="flex items-center gap-2 text-[#5A4D6E]">
            <span className="font-mono shrink-0">{projUsedH}h / {totalH}h pre-existing</span>
            <span className="flex-1"><UtilBar pct={projPct} over={projUsedH > totalH} /></span>
            <span className="font-bold tabular-nums shrink-0">{projPct.toFixed(0)}%</span>
          </div>
          <p className="text-[#8E85A0] italic">Scheduled shifts inside the period will be added on top.</p>
        </div>
      )}

      <div className="flex gap-1.5 justify-end pt-1">
        <Button size="sm" variant="ghost" onClick={onClear} disabled={busy || disabled}
          className="h-7 text-[10px] text-[#D9534F] hover:text-[#D9534F] hover:bg-[#FDECEA]" data-testid={`period-clear-${serviceLine}`}>
          Clear
        </Button>
        <Button size="sm" onClick={onSave} disabled={busy || disabled}
          className="h-7 text-[10px] text-white" style={{ background: accentColor }} data-testid={`period-save-${serviceLine}`}>
          {busy ? 'Saving…' : disabled ? 'Save participant first' : 'Save Period'}
        </Button>
      </div>
    </div>
  );
}

function ParticipantCard({ p, util, onEdit, onDelete }) {
  const hasEmployment = (p.service_lines || []).includes('employment');
  const hasIHCS = (p.service_lines || []).includes('in_home');
  const hasDP = (p.service_lines || []).includes('day_program');
  const hasTemplate = p.schedule_template && (p.schedule_template.days || []).length > 0;
  const u = util || {};
  const anyTracked = (u.day_program?.tracked) || (u.in_home?.tracked) || (u.employment?.tracked);
  const anyOver = (u.day_program?.over || u.day_program?.period_over) || (u.in_home?.over || u.in_home?.period_over) || (u.employment?.over || u.employment?.period_over);
  const anyNear = (u.day_program?.near_limit || u.day_program?.period_near_limit) || (u.in_home?.near_limit || u.in_home?.period_near_limit) || (u.employment?.near_limit || u.employment?.period_near_limit);
  return (
    <Card className={`shadow-sm bg-white hover:-translate-y-px transition-all ${anyOver ? 'border-[#D9534F] ring-1 ring-[#D9534F]/40' : anyNear ? 'border-[#FFB74D]' : 'border-[#DED8EB]'}`} data-testid={`participant-card-${p.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0 ${hasTemplate ? 'bg-[#6B3FA0] ring-2 ring-[#9B7ED8] ring-offset-1' : 'bg-[#9B7ED8]'}`}>
              {p.name?.charAt(0) || '?'}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#2D1B4E]">{p.name}</h3>
              <div className="flex flex-wrap gap-1 mt-1">
                {(p.service_lines || []).map(sl => (
                  <Badge key={sl} className={`text-[10px] ${SERVICE_BADGE[sl] || ''}`}>{SERVICE_LABELS[sl]}</Badge>
                ))}
                {anyOver && <Badge className="text-[10px] bg-[#FDECEA] text-[#D9534F] border border-[#D9534F]">⚠ OVER AUTH</Badge>}
                {!anyOver && anyNear && <Badge className="text-[10px] bg-[#FFF3E0] text-[#E65100] border border-[#FFB74D]">Near Limit</Badge>}
                {(!p.service_lines || p.service_lines.length === 0) && <span className="text-[10px] text-[#8E85A0]">No services assigned</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button variant="ghost" size="icon" onClick={() => onEdit(p)} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`edit-p-${p.id}`}><Edit2 className="h-3 w-3" /></Button>
            <Button variant="ghost" size="icon" onClick={() => onDelete(p.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`delete-p-${p.id}`}><Trash2 className="h-3 w-3" /></Button>
          </div>
        </div>

        {/* Authorization usage bars (this week, Sun-Sat). Only render rows that are tracked. */}
        {anyTracked && (
          <div className="mt-3 space-y-1.5">
            <UtilRow icon={Clock} label="Explore" color="#6B3FA0" util={u.day_program} />
            <UtilRow icon={Clock} label="IHCS" color="#43A047" util={u.in_home} />
            <UtilRow icon={Clock} label="Emp" color="#FFB74D" util={u.employment} />
          </div>
        )}

        {/* Fallback: show legacy plain auth values if no util payload yet (loading) */}
        {!anyTracked && (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#5A4D6E]">
            {hasDP && p.authorized_hours_day_program && (
              <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-[#6B3FA0]" /> Explore: {p.authorized_hours_day_program}h/wk</span>
            )}
            {hasIHCS && p.authorized_hours_ihcs && (
              <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-[#81C784]" /> IHCS: {p.authorized_hours_ihcs}h/wk</span>
            )}
            {hasEmployment && p.authorized_hours_employment && (
              <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-[#FFB74D]" /> Emp: {p.authorized_hours_employment}h/wk</span>
            )}
          </div>
        )}

        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#5A4D6E]">
          {hasEmployment && p.job_site && (
            <span className="flex items-center gap-1"><Briefcase className="h-3 w-3 text-[#FFB74D]" /> {p.job_site}</span>
          )}
          {hasEmployment && p.employment_type && (
            <span className="text-[10px] bg-[#FFF3E0] text-[#E65100] px-1.5 py-0.5 rounded border border-[#FFB74D]">
              {EMPLOYMENT_TYPES[p.employment_type] || p.employment_type}
            </span>
          )}
        </div>
        {p.notes && <p className="text-xs text-[#8E85A0] mt-1 italic">{p.notes}</p>}
        {hasTemplate && (
          <div className="mt-2 flex items-center gap-1.5">
            <CalendarDays className="h-3 w-3 text-[#6B3FA0]" />
            <span className="text-[10px] font-semibold text-[#6B3FA0]">
              Template: {(p.schedule_template.days || []).map(d => d.day?.charAt(0).toUpperCase() + d.day?.slice(1,3)).join(', ')}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ParticipantsPage() {
  const { api } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [utilByPid, setUtilByPid] = useState({});  // {participant_id: {service_line: util_entry}}
  const [periodForms, setPeriodForms] = useState({});  // {service_line: {period_start, period_end, auth_period_hours, already_utilized_hours}}
  const [periodSaving, setPeriodSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');

  // Template state
  const [templateDays, setTemplateDays] = useState([]);
  const [templateService, setTemplateService] = useState('');
  const [templateSaving, setTemplateSaving] = useState(false);

  const fetchParticipants = useCallback(async () => {
    try { const { data } = await api.get('/participants'); setParticipants(data); } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api]);

  // Fetch this-week (Sun-Sat) utilization in parallel; ignore failures (e.g. staff role).
  const fetchUtilization = useCallback(async () => {
    try {
      const { data } = await api.get('/compliance/participants');
      const map = {};
      (data?.participants || []).forEach(p => { map[p.participant_id] = p.utilization || {}; });
      setUtilByPid(map);
    } catch (e) { logger.error(e); }
  }, [api]);

  useEffect(() => { fetchParticipants(); fetchUtilization(); }, [fetchParticipants, fetchUtilization]);

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, notes: p.notes || '', service_lines: p.service_lines || [],
      authorized_hours_ihcs: p.authorized_hours_ihcs || '', authorized_hours_employment: p.authorized_hours_employment || '',
      authorized_hours_day_program: p.authorized_hours_day_program || '',
      job_site: p.job_site || '', employment_type: p.employment_type || ''
    });
    // Seed period forms from existing auth_periods, fall back to empty for each service line
    const ap = p.auth_periods || {};
    const seed = {};
    ['day_program', 'in_home', 'employment'].forEach(sl => {
      const cfg = ap[sl] || {};
      seed[sl] = {
        period_start: cfg.period_start || '',
        period_end: cfg.period_end || '',
        auth_period_hours: cfg.auth_period_hours ?? '',
        already_utilized_hours: cfg.already_utilized_hours ?? '',
      };
    });
    setPeriodForms(seed);
    setPeriodSaving({});
    const tpl = p.schedule_template || {};
    setTemplateDays(tpl.days || []);
    setTemplateService(tpl.service_line || (p.service_lines || [])[0] || '');
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setPeriodForms({});
    setPeriodSaving({});
    setTemplateDays([]);
    setTemplateService('');
    setDialogOpen(true);
  };

  const toggleService = (sl) => setForm(p => ({ ...p, service_lines: p.service_lines.includes(sl) ? p.service_lines.filter(s => s !== sl) : [...p.service_lines, sl] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        authorized_hours_ihcs: form.authorized_hours_ihcs ? parseFloat(form.authorized_hours_ihcs) : null,
        authorized_hours_employment: form.authorized_hours_employment ? parseFloat(form.authorized_hours_employment) : null,
        authorized_hours_day_program: form.authorized_hours_day_program ? parseFloat(form.authorized_hours_day_program) : null,
      };
      if (editing) { await api.put(`/participants/${editing.id}`, payload); }
      else { await api.post('/participants', payload); }
      setDialogOpen(false); fetchParticipants(); fetchUtilization();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this participant?')) return;
    await api.delete(`/participants/${id}`); fetchParticipants();
  };

  // ---- Auth period (per service line) ----
  const updatePeriodField = (sl, field, value) => {
    setPeriodForms(prev => ({ ...prev, [sl]: { ...(prev[sl] || {}), [field]: value } }));
  };

  const saveAuthPeriod = async (sl) => {
    if (!editing) { toast.error('Save the participant first, then set the auth period'); return; }
    const f = periodForms[sl] || {};
    if (!f.period_start || !f.period_end || !f.auth_period_hours) {
      toast.error('Period start, end, and total authorized hours are required');
      return;
    }
    setPeriodSaving(prev => ({ ...prev, [sl]: true }));
    try {
      await api.put(`/compliance/participants/${editing.id}/auth-period/${sl}`, {
        period_start: f.period_start,
        period_end: f.period_end,
        auth_period_hours: parseFloat(f.auth_period_hours) || 0,
        already_utilized_hours: f.already_utilized_hours === '' ? 0 : parseFloat(f.already_utilized_hours) || 0,
      });
      toast.success(`${SERVICE_LABELS[sl]} auth period saved`);
      fetchParticipants();
      fetchUtilization();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save period'); }
    setPeriodSaving(prev => ({ ...prev, [sl]: false }));
  };

  const clearAuthPeriod = async (sl) => {
    if (!editing) return;
    if (!window.confirm(`Clear ${SERVICE_LABELS[sl]} authorization period for ${editing.name}?`)) return;
    setPeriodSaving(prev => ({ ...prev, [sl]: true }));
    try {
      await api.put(`/compliance/participants/${editing.id}/auth-period/${sl}`, { clear: true });
      setPeriodForms(prev => ({ ...prev, [sl]: { period_start: '', period_end: '', auth_period_hours: '', already_utilized_hours: '' } }));
      toast.success('Period cleared');
      fetchParticipants();
      fetchUtilization();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setPeriodSaving(prev => ({ ...prev, [sl]: false }));
  };

  // Template handlers
  const toggleTemplateDay = (dayKey) => {
    const exists = templateDays.find(d => d.day === dayKey);
    if (exists) {
      setTemplateDays(prev => prev.filter(d => d.day !== dayKey));
    } else {
      setTemplateDays(prev => [...prev, { day: dayKey, time_in: '09:00', time_out: '17:00', location: '' }]);
    }
  };

  const updateTemplateDay = (dayKey, field, value) => {
    setTemplateDays(prev => prev.map(d => d.day === dayKey ? { ...d, [field]: value } : d));
  };

  const handleSaveTemplate = async () => {
    if (!editing) return;
    setTemplateSaving(true);
    try {
      if (templateDays.length === 0) {
        await api.delete(`/participants/${editing.id}/template`);
        toast.success('Template removed');
      } else {
        await api.put(`/participants/${editing.id}/template`, { service_line: templateService, days: templateDays });
        toast.success('Template saved');
      }
      fetchParticipants();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving template'); }
    setTemplateSaving(false);
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let result = participants.filter(p => {
      const matchSearch = !q || p.name.toLowerCase().includes(q) || (p.notes || '').toLowerCase().includes(q) || (p.job_site || '').toLowerCase().includes(q);
      const matchFilter = filterTab === 'all' || (p.service_lines || []).includes(filterTab);
      return matchSearch && matchFilter;
    });
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name_asc': return (a.name || '').localeCompare(b.name || '');
        case 'name_desc': return (b.name || '').localeCompare(a.name || '');
        case 'services': return (b.service_lines || []).length - (a.service_lines || []).length || (a.name || '').localeCompare(b.name || '');
        default: return (a.name || '').localeCompare(b.name || '');
      }
    });
    return result;
  }, [participants, search, filterTab, sortBy]);

  const serviceCounts = useMemo(() => {
    const c = { all: participants.length, day_program: 0, in_home: 0, employment: 0 };
    participants.forEach(p => (p.service_lines || []).forEach(sl => { c[sl] = (c[sl] || 0) + 1; }));
    return c;
  }, [participants]);

  const showEmploymentFields = form.service_lines.includes('employment');
  const showIHCSFields = form.service_lines.includes('in_home');
  const showDayProgramFields = form.service_lines.includes('day_program');

  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="participants-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-4xl tracking-tight font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>Participants</h1>
          <p className="text-xs sm:text-base text-[#5A4D6E] mt-1">{participants.length} individuals</p>
        </div>
        <Button onClick={openCreate} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2 h-10" data-testid="add-participant-btn">
          <Plus className="h-4 w-4" /> Add Participant
        </Button>
      </div>

      {/* Filter tabs + search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <Tabs value={filterTab} onValueChange={setFilterTab} className="flex-1 min-w-0">
          <TabsList className="bg-[#F0EDF6] border border-[#DED8EB] w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
            <TabsTrigger value="all" className="data-[state=active]:bg-white gap-1 shrink-0">All <Badge className="text-[10px] bg-[#DED8EB] text-[#5A4D6E] h-5 rounded-full">{serviceCounts.all}</Badge></TabsTrigger>
            <TabsTrigger value="day_program" className="data-[state=active]:bg-white gap-1 shrink-0">Explore <Badge className="text-[10px] bg-[#EDE7F6] text-[#6B3FA0] h-5 rounded-full">{serviceCounts.day_program}</Badge></TabsTrigger>
            <TabsTrigger value="employment" className="data-[state=active]:bg-white gap-1 shrink-0">Emp <Badge className="text-[10px] bg-[#FFF3E0] text-[#E65100] h-5 rounded-full">{serviceCounts.employment}</Badge></TabsTrigger>
            <TabsTrigger value="in_home" className="data-[state=active]:bg-white gap-1 shrink-0">IHCS <Badge className="text-[10px] bg-[#E8F5E9] text-[#1B5E20] h-5 rounded-full">{serviceCounts.in_home}</Badge></TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8E85A0]" />
            <Input placeholder="Search participants..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 border-[#DED8EB] bg-white h-10 text-sm" data-testid="participant-search" />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-28 sm:w-40 border-[#DED8EB] bg-white h-10 text-sm shrink-0" data-testid="participant-sort">
              <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-[#8E85A0]" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name_asc">Name A-Z</SelectItem>
              <SelectItem value="name_desc">Name Z-A</SelectItem>
              <SelectItem value="services">By Services</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Cards grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-24 bg-[#DED8EB] rounded-lg animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-[#8E85A0]">
          <UserCheck className="h-10 w-10 mx-auto mb-2 opacity-50" />
          <p>{search ? 'No matches' : 'No participants yet'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="participants-grid">
          {filtered.map(p => <ParticipantCard key={p.id} p={p} util={utilByPid[p.id]} onEdit={openEdit} onDelete={handleDelete} />)}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[85vh] flex flex-col" data-testid="participant-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editing ? 'Edit Participant' : 'Add Participant'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 overflow-y-auto flex-1 pr-1">
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Name</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="First name" className="border-[#DED8EB]" data-testid="p-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Service Lines</Label>
              {Object.entries(SERVICE_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center gap-2">
                  <Checkbox checked={form.service_lines.includes(key)} onCheckedChange={() => toggleService(key)} data-testid={`p-sl-${key}`} />
                  <label className="text-sm text-[#5A4D6E] cursor-pointer">{label}</label>
                </div>
              ))}
            </div>

            {/* Day Program (Explore) fields */}
            {showDayProgramFields && (
              <div className="p-3 rounded-lg bg-[#EDE7F6]/50 border border-[#9B7ED8] space-y-3">
                <p className="text-sm font-semibold text-[#6B3FA0]">Explore Program (Day Program) Details</p>
                <div className="space-y-2">
                  <Label className="text-[#2D1B4E] font-medium text-sm">
                    Authorized Hours / Week <span className="text-[#8E85A0] font-normal">(optional)</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" step="0.25" value={form.authorized_hours_day_program}
                      onChange={e => setForm(p => ({ ...p, authorized_hours_day_program: e.target.value }))}
                      placeholder="e.g. 30" className="border-[#DED8EB] w-32" data-testid="p-dp-hours" />
                    <span className="text-xs text-[#8E85A0] font-mono">
                      {form.authorized_hours_day_program ? `= ${(parseFloat(form.authorized_hours_day_program) * 4).toFixed(0)} units/wk` : ''}
                    </span>
                  </div>
                  <p className="text-[10px] text-[#8E85A0]">1 unit = 15 min · 4 units = 1 hour</p>
                </div>
                <AuthPeriodEditor
                  serviceLine="day_program" label="Explore" accentColor="#6B3FA0"
                  value={periodForms.day_program}
                  onChange={(f, v) => updatePeriodField('day_program', f, v)}
                  onSave={() => saveAuthPeriod('day_program')}
                  onClear={() => clearAuthPeriod('day_program')}
                  busy={periodSaving.day_program}
                  disabled={!editing}
                />
              </div>
            )}

            {/* IHCS fields */}
            {showIHCSFields && (
              <div className="p-3 rounded-lg bg-[#E8F5E9]/50 border border-[#81C784] space-y-3">
                <p className="text-sm font-semibold text-[#1B5E20]">IHCS Details</p>
                <div className="space-y-2">
                  <Label className="text-[#2D1B4E] font-medium text-sm">Authorized Hours/Week <span className="text-[#8E85A0] font-normal">(optional)</span></Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" step="0.25" value={form.authorized_hours_ihcs} onChange={e => setForm(p => ({ ...p, authorized_hours_ihcs: e.target.value }))}
                      placeholder="Can add later" className="border-[#DED8EB] w-32" data-testid="p-ihcs-hours" />
                    <span className="text-xs text-[#8E85A0] font-mono">
                      {form.authorized_hours_ihcs ? `= ${(parseFloat(form.authorized_hours_ihcs) * 4).toFixed(0)} units/wk` : ''}
                    </span>
                  </div>
                </div>
                <AuthPeriodEditor
                  serviceLine="in_home" label="IHCS" accentColor="#43A047"
                  value={periodForms.in_home}
                  onChange={(f, v) => updatePeriodField('in_home', f, v)}
                  onSave={() => saveAuthPeriod('in_home')}
                  onClear={() => clearAuthPeriod('in_home')}
                  busy={periodSaving.in_home}
                  disabled={!editing}
                />
              </div>
            )}

            {/* Employment fields */}
            {showEmploymentFields && (
              <div className="p-3 rounded-lg bg-[#FFF3E0]/50 border border-[#FFB74D] space-y-3">
                <p className="text-sm font-semibold text-[#E65100]">Employment Details</p>
                <div className="space-y-2">
                  <Label className="text-[#2D1B4E] font-medium text-sm">Authorized Hours/Week <span className="text-[#8E85A0] font-normal">(optional)</span></Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" step="0.25" value={form.authorized_hours_employment} onChange={e => setForm(p => ({ ...p, authorized_hours_employment: e.target.value }))}
                      placeholder="Can add later" className="border-[#DED8EB] w-32" data-testid="p-emp-hours" />
                    <span className="text-xs text-[#8E85A0] font-mono">
                      {form.authorized_hours_employment ? `= ${(parseFloat(form.authorized_hours_employment) * 4).toFixed(0)} units/wk` : ''}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#2D1B4E] font-medium text-sm">Job Site <span className="text-[#8E85A0] font-normal">(optional)</span></Label>
                  <Input value={form.job_site} onChange={e => setForm(p => ({ ...p, job_site: e.target.value }))}
                    placeholder="Can add later" className="border-[#DED8EB]" data-testid="p-job-site" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#2D1B4E] font-medium text-sm">Service Type <span className="text-[#8E85A0] font-normal">(optional)</span></Label>
                  <Select value={form.employment_type} onValueChange={v => setForm(p => ({ ...p, employment_type: v }))}>
                    <SelectTrigger className="border-[#DED8EB]" data-testid="p-emp-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="development_services">Development Services (The Fields Method)</SelectItem>
                      <SelectItem value="job_support">Job Support Services</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <AuthPeriodEditor
                  serviceLine="employment" label="Employment" accentColor="#E65100"
                  value={periodForms.employment}
                  onChange={(f, v) => updatePeriodField('employment', f, v)}
                  onSave={() => saveAuthPeriod('employment')}
                  onClear={() => clearAuthPeriod('employment')}
                  busy={periodSaving.employment}
                  disabled={!editing}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Notes</Label>
              <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Any notes..." className="border-[#DED8EB]" data-testid="p-notes" />
            </div>

            {/* Schedule Template */}
            {editing && (
              <div className="p-3 rounded-lg bg-[#F7F4EF] border border-[#DED8EB] space-y-3" data-testid="template-section">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-[#6B3FA0]" />
                    <Label className="text-[#2D1B4E] font-semibold text-sm">Weekly Schedule Template</Label>
                  </div>
                  {templateDays.length > 0 && (
                    <button onClick={() => setTemplateDays([])} className="text-[10px] text-[#8E85A0] hover:text-[#D9534F]">Clear all</button>
                  )}
                </div>
                <p className="text-[10px] text-[#8E85A0]">Define a standard weekly schedule. Use "Apply Templates" on the Schedule page to auto-generate shifts.</p>

                <div className="space-y-2">
                  <Label className="text-xs text-[#5A4D6E]">Service Line</Label>
                  <Select value={templateService} onValueChange={setTemplateService}>
                    <SelectTrigger className="border-[#DED8EB] h-8 text-sm" data-testid="template-service"><SelectValue placeholder="Select service" /></SelectTrigger>
                    <SelectContent>
                      {(form.service_lines || []).map(sl => (
                        <SelectItem key={sl} value={sl}>{SERVICE_LABELS[sl]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-[#5A4D6E]">Days</Label>
                  <div className="flex gap-1.5">
                    {DAY_KEYS.map(([key, label]) => {
                      const active = templateDays.some(d => d.day === key);
                      return (
                        <button key={key} type="button" onClick={() => toggleTemplateDay(key)}
                          className={`w-9 h-9 rounded-lg text-xs font-bold transition-all ${active ? 'bg-[#6B3FA0] text-white shadow-sm' : 'bg-white text-[#8E85A0] border border-[#DED8EB] hover:border-[#9B7ED8]'}`}
                          data-testid={`tpl-day-${key}`}>{label.charAt(0)}</button>
                      );
                    })}
                  </div>
                </div>

                {templateDays.length > 0 && (
                  <div className="space-y-2">
                    {DAY_KEYS.filter(([key]) => templateDays.some(d => d.day === key)).map(([key, label]) => {
                      const dayData = templateDays.find(d => d.day === key) || {};
                      return (
                        <div key={key} className="flex items-center gap-2 p-2 rounded-md bg-white border border-[#DED8EB]" data-testid={`tpl-row-${key}`}>
                          <span className="text-xs font-semibold text-[#2D1B4E] w-8">{label}</span>
                          <Input type="time" value={dayData.time_in || '09:00'} onChange={e => updateTemplateDay(key, 'time_in', e.target.value)}
                            className="border-[#DED8EB] h-7 w-24 text-xs" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid={`tpl-in-${key}`} />
                          <span className="text-[10px] text-[#8E85A0]">to</span>
                          <Input type="time" value={dayData.time_out || '17:00'} onChange={e => updateTemplateDay(key, 'time_out', e.target.value)}
                            className="border-[#DED8EB] h-7 w-24 text-xs" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid={`tpl-out-${key}`} />
                          <Input value={dayData.location || ''} onChange={e => updateTemplateDay(key, 'location', e.target.value)}
                            placeholder="Location" className="border-[#DED8EB] h-7 flex-1 text-xs" data-testid={`tpl-loc-${key}`} />
                        </div>
                      );
                    })}
                  </div>
                )}

                <Button size="sm" onClick={handleSaveTemplate} disabled={templateSaving || !templateService}
                  className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 h-8 text-xs" data-testid="save-template-btn">
                  {templateSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  {templateDays.length === 0 ? 'Remove Template' : 'Save Template'}
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#DED8EB]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !form.name} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="p-save-btn">
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
